import { ENV_BASE_URL } from 'env';
import { TFilterBarItem } from 'common/types';
import { getCurrentUrl } from 'common/utils/getUrl';
import {
    IBasicGroupFragment,
    IExtraCategoryChoiceValue,
    IExtraCategoryOption,
    IGroupConditionFragment,
    IGroupListItemFragment,
} from 'graphql/types';
import { getUrl } from 'route/utils/getUrl';
import {
    IGroupConditionInput,
    IOldGroupConditionInput,
    TGroupListItem,
} from 'user/types';

/*
 * GroupConditionFilter: Transforms group conditions to usable inputs for the group condition filter component
 */
export function getGroupConditionsFilterInputs(
    groupConditions?: IGroupConditionFragment[],
    oldGroupConditions?: boolean
): IGroupConditionInput[] {
    return (
        groupConditions
            ?.map((groupCondition) => {
                const {
                    extraCategoryValues,
                    groups: groupsData,
                    id,
                } = groupCondition;

                const values = extraCategoryValues
                    .map((extraCategoryValue) => {
                        const {
                            category: { id: extraCategoryId, categoryType },
                        } = extraCategoryValue;

                        if (
                            categoryType === IExtraCategoryOption.Boolean ||
                            categoryType === IExtraCategoryOption.Datetime
                        ) {
                            return null;
                        }

                        const { choiceValue } =
                            extraCategoryValue as IExtraCategoryChoiceValue;

                        const stringValue = choiceValue?.stringValue;

                        return {
                            id: extraCategoryId,
                            stringValue,
                            color: choiceValue?.color,
                        };
                    })
                    .filter(Boolean);

                return {
                    id,
                    extraCategoryValues: values,
                    groups: (groupsData.edges
                        ?.map((edge) =>
                            oldGroupConditions ? edge?.node : edge?.node?.id
                        )
                        .filter(Boolean) ||
                        []) as IGroupConditionInput['groups'],
                    minMaxFilters: groupCondition.minMaxFilters
                        ?.map((minMaxFilter) => {
                            if (!minMaxFilter.group?.id) return null;

                            return {
                                groupId: minMaxFilter.group?.id,
                                min: Number(minMaxFilter.min),
                                max: Number(minMaxFilter.max),
                            };
                        })
                        .filter(Boolean),
                };
            })
            .filter(Boolean) || []
    );
}

/*
 * GroupConditionFilter: Transforms group conditions inputs to its actual mutation inputs
 *
 * MinMaxFilters for groups that are no longer in the condition will have their
 * min/max set to null so the backend knows to delete them.
 */
export function getGroupConditionMutationInputs(
    groupConditionInputs?: (IGroupConditionInput | IOldGroupConditionInput)[],
    oldGroupConditions?: boolean
) {
    return groupConditionInputs?.map((groupConditionInput) => {
        const { extraCategoryValues, groups } = groupConditionInput;

        const groupIds = oldGroupConditions
            ? (groups as TGroupListItem[])
                  .map((group) => group.id)
                  .filter(Boolean)
            : (groups as string[]);

        // Null out minMaxFilters for groups no longer in the condition
        const minMaxFilters = groupConditionInput.minMaxFilters?.map(
            (filter) => {
                if (!groupIds.includes(filter.groupId)) {
                    return { ...filter, min: null, max: null };
                }

                return filter;
            }
        );

        return {
            extraCategoryValues:
                extraCategoryValues?.map(({ id, stringValue }) => ({
                    id,
                    stringValue,
                })) || [],
            groups: groupIds,
            minMaxFilters,
        };
    });
}

/*
 *  GroupConditionFilter: Get the filters that belong to the selected group condition filter
 */
export function getSelectedGroupConditionFilters(
    groupConditionInputValues?: IGroupConditionInput['extraCategoryValues'],
    extraCategoriesFilters?: TFilterBarItem[]
): TFilterBarItem[] {
    if (!groupConditionInputValues?.length || !extraCategoriesFilters?.length) {
        return [];
    }

    return groupConditionInputValues
        .map((groupConditionInputValue) => {
            const { id, stringValue } = groupConditionInputValue;

            const extraCategoryFilter = extraCategoriesFilters.find(
                (extraCategoryFilter) =>
                    extraCategoryFilter.children?.find(
                        ({ extraCategoryData }) => extraCategoryData?.id === id
                    )
            );

            if (!extraCategoryFilter) return null;

            const { children } = extraCategoryFilter;

            if (!children?.length) return null;

            const filter = children.find(
                (child) => child.value === stringValue
            );

            if (!filter) return null;

            return filter;
        })
        .filter(Boolean);
}

/*
 *  Get the added groups
 */
export const getAddedGroupIds = (
    initialGroups: (IGroupListItemFragment | IBasicGroupFragment)[],
    newGroups: (IGroupListItemFragment | IBasicGroupFragment)[]
) => {
    const addList = newGroups
        .filter(
            (newGroup) =>
                !initialGroups.some(
                    (initialGroup) => initialGroup.id === newGroup.id
                )
        )
        .map((group) => group.id);

    return !!addList.length ? addList : undefined;
};

/*
 *  Get the removed groups
 */
export const getRemovedGroupIds = (
    initialGroups: (IGroupListItemFragment | IBasicGroupFragment)[],
    newGroups: (IGroupListItemFragment | IBasicGroupFragment)[]
) => {
    const removeList = initialGroups
        .filter(
            (initialGroup) =>
                !newGroups.some((newGroup) => newGroup.id === initialGroup.id)
        )
        .map((group) => group.id);

    return !!removeList.length ? removeList : undefined;
};

export function getGroupUserImportUrl(groupId: string) {
    // Build the scorm url with current url as next
    const nextUrl = getCurrentUrl();
    const userImportUrl = getUrl('MANAGEMENT_GROUPS_USER_IMPORT');

    return `${ENV_BASE_URL}${userImportUrl}/${groupId}?next=${nextUrl}`;
}
